import { isElementInViewport } from '@theme/utilities';

/**
 * Barre d'ajout au panier collante.
 *
 * ⚠️ ARCHITECTURE, à lire avant de modifier.
 *
 * Le thème rendait le bouton d'ajout au panier À L'INTÉRIEUR de <sticky-add-to-cart>
 * et faisait passer tout l'élément en `position: fixed` pour le coller en bas. Il n'y
 * avait donc qu'UN SEUL bouton, qui quittait la page dès qu'il devenait collant : ça
 * laissait un trou dans la colonne d'achat, à l'endroit où il aurait dû être.
 *
 * Ici le bouton d'origine ne bouge plus jamais. On construit une barre séparée,
 * ajoutée au <body>, qui contient une COPIE visuelle du bouton. Un clic sur la copie
 * est relayé au vrai bouton. Un seul formulaire, donc aucun risque de double envoi,
 * et la colonne d'achat reste intacte.
 *
 * Comportement voulu :
 *  - tant que le bouton d'origine n'a pas été dépassé, aucune barre ;
 *  - une fois dépassé, la barre s'affiche et NE SE RECACHE PLUS, même si le visiteur
 *    remonte en haut de la fiche (verrou) ;
 *  - sauf sur le pied de page, qu'elle ne doit pas recouvrir.
 */
class StickyAddToCart extends HTMLElement {
  constructor() {
    super();

    this.footer = null;
    this.trigger = null;
    this.bar = null;
    this.observer = null;
    this.ticking = false;
    this.unlocked = false;
  }

  connectedCallback() {
    this.footer = document.querySelector('[data-ref="footer"]');
    this.trigger = this.querySelector('[data-ref="add-to-cart-button"]');

    this.#buildBar();
    this.#update();

    this.onScroll = () => this.#requestUpdate();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    // Le libellé et l'état du bouton changent avec la variante et pendant l'ajout au
    // panier : on garde la copie synchronisée avec l'original.
    if (this.trigger) {
      this.observer = new MutationObserver(() => this.#syncBar());
      this.observer.observe(this.trigger, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'class'],
      });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.observer?.disconnect();
    this.bar?.remove();
    this.bar = null;
  }

  /** Construit la barre fixe et sa copie du bouton. */
  #buildBar() {
    const content = this.querySelector('.sticky-add-to-cart__content');
    if (!content || this.bar) return;

    this.bar = document.createElement('div');
    this.bar.className = 'bz-atc-bar color-scheme-1';
    this.bar.dataset.active = 'false';

    const inner = document.createElement('div');
    inner.className = 'bz-atc-bar__inner';
    this.bar.appendChild(inner);

    this.barInner = inner;
    this.#syncBar();

    // Un clic sur la copie déclenche le vrai bouton, seul rattaché au formulaire.
    // ⚠️ On ne relaie QUE la copie du bouton d'ajout au panier. Relayer n'importe quel
    // <button> de la barre transformerait un clic sur « + » ou « − » en ajout au panier.
    this.bar.addEventListener('click', (event) => {
      const button = event.target.closest('.bz-atc-bar__cta');
      if (!button || button.disabled) return;
      event.preventDefault();
      this.trigger?.click();
    });

    document.body.appendChild(this.bar);
  }

  /** Recopie le contenu du bouton d'origine, débarrassé de tout ce qui doit rester unique. */
  #syncBar() {
    const content = this.querySelector('.sticky-add-to-cart__content');
    if (!content || !this.barInner) return;

    const copy = content.cloneNode(true);

    // Repérer la copie du bouton d'ajout au panier AVANT de nettoyer les data-ref,
    // c'est le seul élément dont le clic sera relayé.
    const cta = copy.querySelector('[data-ref="add-to-cart-button"]');
    cta?.classList.add('bz-atc-bar__cta');

    // Le sélecteur de quantité et la liste d'envies n'ont rien à faire dans une barre
    // collante, et leurs boutons seraient des pièges à clic.
    copy.querySelectorAll('[class*="quantity"], [class*="wishlist"]').forEach((el) => el.remove());
    copy.querySelectorAll('button').forEach((el) => {
      if (el !== cta) el.remove();
    });

    // Rien dans la copie ne doit être trouvable par les scripts du thème, ni soumettre
    // quoi que ce soit : ce n'est qu'une façade.
    copy.querySelectorAll('input, select, textarea').forEach((el) => el.remove());
    copy.querySelectorAll('[data-ref]').forEach((el) => el.removeAttribute('data-ref'));
    copy.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    copy.querySelectorAll('[klaviyo-bis-trigger]').forEach((el) => el.removeAttribute('klaviyo-bis-trigger'));
    if (cta) {
      cta.type = 'button';
      cta.disabled = this.trigger ? this.trigger.disabled : false;
    }

    this.barInner.replaceChildren(...copy.childNodes);
  }

  #requestUpdate() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.#update();
    });
  }

  #update() {
    if (!this.bar) return;

    const rect = this.getBoundingClientRect();

    // Le bouton d'origine a-t-il été dépassé par le haut, au moins une fois ?
    // Il ne quitte jamais le flux, donc sa position est stable : pas d'oscillation.
    if (!this.unlocked && rect.bottom < 0) {
      this.unlocked = true;
    }

    // Le vrai bouton est-il sous les yeux du visiteur ? Si oui, la barre ferait
    // doublon : deux « Ajouter au panier » à l'écran en même temps.
    const buttonVisible = rect.bottom > 0 && rect.top < window.innerHeight;

    const footerVisible = this.footer ? isElementInViewport(this.footer) : false;
    const shouldShow = this.unlocked && !buttonVisible && !footerVisible;

    const next = shouldShow ? 'true' : 'false';
    if (this.bar.dataset.active !== next) {
      this.bar.dataset.active = next;
      this.bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
